// @flow

import type {
  MerchandisingV2State,
  MerchandisingV2Action
} from 'stores/flow-type/merchandising-v2.type';

import {
  ENABLED_MERCHANDISING_V2_RULES_REQUEST,
  ENABLED_MERCHANDISING_V2_RULES_SUCCESS,
  ENABLED_MERCHANDISING_V2_RULES_FAILURE,
  GET_MERCHANDISING_V2_RULES_REQUEST,
  GET_MERCHANDISING_V2_RULES_SUCCESS,
  GET_MERCHANDISING_V2_RULES_FAILURE,
  GET_MERCHANDISING_V2_RULE_REQUEST,
  GET_MERCHANDISING_V2_RULE_SUCCESS,
  GET_MERCHANDISING_V2_RULE_FAILURE,
  CREATE_MERCHANDISING_RULE_V2_REQUEST,
  CREATE_MERCHANDISING_RULE_V2_SUCCESS,
  CREATE_MERCHANDISING_RULE_V2_FAILURE,
  UPDATE_MERCHANDISING_V2_RULE_REQUEST,
  UPDATE_MERCHANDISING_V2_RULE_SUCCESS,
  UPDATE_MERCHANDISING_V2_RULE_FAILURE,
  DELETE_MERCHANDISING_V2_RULE_REQUEST,
  DELETE_MERCHANDISING_V2_RULE_SUCCESS,
  DELETE_MERCHANDISING_V2_RULE_FAILURE,
  DUPLICATE_MERCHANDISING_V2_RULE_REQUEST,
  DUPLICATE_MERCHANDISING_V2_RULE_SUCCESS,
  DUPLICATE_MERCHANDISING_V2_RULE_FAILURE,
  PREVIEW_MERCHANDISING_V2_RULE_REQUEST,
  PREVIEW_MERCHANDISING_V2_RULE_SUCCESS,
  PREVIEW_MERCHANDISING_V2_RULE_FAILURE,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_REQUEST,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_SUCCESS,
  GET_PRODUCTS_WITH_FILTER_CONDITIONS_FAILURE,
  GET_PRODUCTS_REQUEST,
  GET_PRODUCTS_SUCCESS,
  GET_PRODUCTS_FAILURE,
  RESET_MERCHANDISING_V2_RULE,
  GET_EXISTS_UNIVERSAL_RULE_SUCCESS,
  GET_EXISTS_UNIVERSAL_RULE_FAILURE,
  GET_EXISTS_UNIVERSAL_RULE_REQUEST,
  CHECK_DUPLICATE_TERM_SUCCESS
} from 'src/stores/flow-type/merchandising-v2.type';
import { CHECK_DUPLICATE_TERM_REQUEST } from 'stores/flow-type/merchandising-v2.type';

const initialState: MerchandisingV2State = {
  loading: false,
  saving: false,
  deleting: false,
  duplicating: false,
  renderingPreview: false,
  loadingProducts: false,
  success: false,
  saveSuccess: false,
  renderingPreviewSuccess: false,
  previewRequestBody: '',
  loadingProductsSuccess: false,
  error: null,
  total: 0,
  totalRules: 0,
  merchandisingRules: [],
  merchandisingRule: {},
  duplicateRule: {},
  introVersionModal: false,
  previewProducts: [],
  previewTotalProduct: 0,
  productsWithFilterConditions: [],
  productsWithFilterConditionsCount: 0,
  loadingMerchandisingProducts: false,
  loadingProductsMerchandisingSuccess: false,
  products: [],
  totalProducts: 0,
  totalAllUnlockRules: 0,
  panelID: 0,
  existUniversalRule: false,
  enableSortByRelevance: true,
  enableSortByManual: true,
  duplicateTerm: {},
  loadingDuplicateTerm: false
};

export const mapRuleData = rule => ({
  ...rule,
  queriesType: rule.isUniversal ? 'all' : 'specific'
});

const merchandisingV2Reducers = (
  state: MerchandisingV2State = initialState,
  action: MerchandisingV2Action
) => {
  switch (action.type) {
    /* #region: enabled merchandising v2 rules */
    case ENABLED_MERCHANDISING_V2_RULES_REQUEST:
      return {
        ...state,
        success: false,
        loading: true
      };
    case ENABLED_MERCHANDISING_V2_RULES_SUCCESS: {
      return {
        ...state,
        loading: false,
        success: true,
        error: undefined
      };
    }
    case ENABLED_MERCHANDISING_V2_RULES_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    /* #end-region */

    /* #region: Get merchandising v2 rules */
    case GET_MERCHANDISING_V2_RULES_REQUEST:
      return {
        ...state,
        success: false,
        loading: true,
        enableSortByRelevance: true
      };
    case GET_MERCHANDISING_V2_RULES_SUCCESS: {
      const { data = {} } = action.payload;
      let {
        data: merchandisingRules,
        total,
        totalRules,
        enableSortByRelevance,
        enableSortByManual,
        totalAllUnlockRules
      } = data;

      return {
        ...state,
        loading: false,
        success: true,
        error: undefined,
        merchandisingRules: merchandisingRules.map(r => mapRuleData(r)),
        enableSortByRelevance,
        enableSortByManual,
        totalAllUnlockRules,
        total,
        totalRules
      };
    }
    case GET_MERCHANDISING_V2_RULES_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Get merchandising v2 rule */
    // Get merchandising rule_V2
    case GET_MERCHANDISING_V2_RULE_REQUEST:
      return { ...state, success: false, loading: true };
    case GET_MERCHANDISING_V2_RULE_SUCCESS: {
      const { data: merchandisingRule } = action.payload;
      return {
        ...state,
        loading: false,
        success: true,
        merchandisingRule: mapRuleData(merchandisingRule),
        error: undefined
      };
    }
    case GET_MERCHANDISING_V2_RULE_FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Create merchandising v2 rule */
    case CREATE_MERCHANDISING_RULE_V2_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true
      };
    case CREATE_MERCHANDISING_RULE_V2_SUCCESS: {
      const { data: merchandisingRule } = action.payload;

      return {
        ...state,
        saving: false,
        merchandisingRule,
        saveSuccess: true,
        error: undefined
      };
    }
    case CREATE_MERCHANDISING_RULE_V2_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Update merchandising v2 rule */
    case UPDATE_MERCHANDISING_V2_RULE_REQUEST:
      return {
        ...state,
        saveSuccess: false,
        saving: true
      };
    case UPDATE_MERCHANDISING_V2_RULE_SUCCESS: {
      const { data, actionName } = action.payload;
      const { data: merchandisingRule } = data;
      // Save updated merchandising to store
      if (actionName !== 'changeStatus') {
        return {
          ...state,
          saving: false,
          saveSuccess: true,
          merchandisingRule,
          error: undefined
        };
      }
      return {
        ...state,
        saving: false,
        saveSuccess: true,
        error: undefined
      };
    }
    case UPDATE_MERCHANDISING_V2_RULE_FAILURE:
      return {
        ...state,
        saving: false,
        saveSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Delete merchandising v2 rule */
    case DELETE_MERCHANDISING_V2_RULE_REQUEST:
      return {
        ...state,
        success: false,
        deleting: true
      };
    case DELETE_MERCHANDISING_V2_RULE_SUCCESS: {
      const { data = {} } = action.payload;
      const { rule = {} } = data;
      const totalAllUnlockRules = state.totalAllUnlockRules > 0 ? state.totalAllUnlockRules - 1 : 0;
      return {
        ...state,
        totalAllUnlockRules,
        deleting: false,
        success: true,
        total: state.total - 1,
        totalRules: state.totalRules - 1,
        merchandisingRules: (state.merchandisingRules || []).filter(r => r.id !== rule.id),
        error: undefined
      };
    }
    case DELETE_MERCHANDISING_V2_RULE_FAILURE:
      return {
        ...state,
        deleting: false,
        success: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Duplicate merchandising v2 rule */
    case DUPLICATE_MERCHANDISING_V2_RULE_REQUEST:
      return {
        ...state,
        success: false,
        duplicating: true
      };
    case DUPLICATE_MERCHANDISING_V2_RULE_SUCCESS: {
      const { data: duplicateRule } = action.payload;
      const totalAllUnlockRules = state.totalAllUnlockRules + 1;

      return {
        ...state,
        duplicating: false,
        duplicateRule,
        totalAllUnlockRules,
        success: true,
        error: undefined
      };
    }
    case DUPLICATE_MERCHANDISING_V2_RULE_FAILURE:
      return {
        ...state,
        duplicating: false,
        success: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Preview merchandising v2 rule */
    case PREVIEW_MERCHANDISING_V2_RULE_REQUEST:
      return {
        ...state,
        renderingPreviewSuccess: false,
        renderingPreview: true
      };
    case PREVIEW_MERCHANDISING_V2_RULE_SUCCESS: {
      const { data, requestBody } = action.payload;
      const { products = [], total_product = 0 } = data;

      return {
        ...state,
        renderingPreview: false,
        previewProducts: products,
        previewRequestBody: JSON.stringify({ ...requestBody, page: 0 }),
        previewTotalProduct: total_product,
        renderingPreviewSuccess: true
      };
    }
    case PREVIEW_MERCHANDISING_V2_RULE_FAILURE:
      return {
        ...state,
        renderingPreview: false,
        renderingPreviewSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Preview merchandising v2 rule */
    case GET_PRODUCTS_WITH_FILTER_CONDITIONS_REQUEST:
      return {
        ...state,
        loadingProductsSuccess: false,
        loadingProducts: true
      };
    case GET_PRODUCTS_WITH_FILTER_CONDITIONS_SUCCESS: {
      const { products = [], total_product = 0 } = action.payload;

      return {
        ...state,
        loadingProducts: false,
        productsWithFilterConditions: products,
        productsWithFilterConditionsCount: total_product,
        loadingProductsSuccess: true
      };
    }
    case GET_PRODUCTS_WITH_FILTER_CONDITIONS_FAILURE:
      return {
        ...state,
        loadingProducts: false,
        loadingProductsSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Get products list */
    case GET_PRODUCTS_REQUEST:
      return {
        ...state,
        loadingProductsMerchandisingSuccess: false,
        loadingMerchandisingProducts: true
      };
    case GET_PRODUCTS_SUCCESS: {
      const { data = [], total = 0 } = action.payload;

      return {
        ...state,
        loadingMerchandisingProducts: false,
        products: data,
        totalProducts: total,
        loadingProductsMerchandisingSuccess: true
      };
    }
    case GET_PRODUCTS_FAILURE:
      return {
        ...state,
        loadingMerchandisingProducts: false,
        loadingProductsMerchandisingSuccess: false,
        error: action.payload
      };
    /* #endregion */

    /* #region: Reset merchandising v2 rule */
    case RESET_MERCHANDISING_V2_RULE:
      return initialState;
    /* #endregion */

    case 'PRODUCT_TOUR_MERCHANDISING_V2':
      return {
        ...state,
        introVersionModal: action.payload
      };
    case 'SET_TAB_RULES_MERCHANDISING_V2':
      return {
        ...state,
        panelID: action.payload
      };
    case GET_EXISTS_UNIVERSAL_RULE_REQUEST:
      return state;
    case GET_EXISTS_UNIVERSAL_RULE_SUCCESS:
      return {
        ...state,
        existUniversalRule: action.payload.isExists
      };
    case GET_EXISTS_UNIVERSAL_RULE_FAILURE:
      return {
        ...state,
        existUniversalRule: true
      };
    case CHECK_DUPLICATE_TERM_REQUEST:
      return {
        ...state,
        loadingDuplicateTerm: true
      };
    case CHECK_DUPLICATE_TERM_SUCCESS: {
      const { data, searchTerm } = action.payload;
      return {
        ...state,
        duplicateTerm: {
          ...state.duplicateTerm,
          [searchTerm]: data
        },
        loadingDuplicateTerm: false
      };
    }
    default:
      return state;
  }
};
export default merchandisingV2Reducers;
